import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Copy, ExternalLink, Flag } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";

const ORIGIN = process.env.REACT_APP_BACKEND_URL;
const send = (token, type, reason) => api.post("/ads/events", { token, type, reason }).catch(() => ({ data: {} }));

function useViewable(ref, token) {
  useEffect(() => {
    const el = ref.current;
    if (!token || !el || !("IntersectionObserver" in window)) return undefined;
    let timer = null;
    let done = false;
    const io = new IntersectionObserver(([e]) => {
      if (done) return;
      if (e.intersectionRatio >= 0.5) { timer = timer || setTimeout(() => { done = true; send(token, "viewable"); io.disconnect(); }, 1000); }
      else { clearTimeout(timer); timer = null; }
    }, { threshold: [0, 0.5, 1] });
    io.observe(el);
    return () => { clearTimeout(timer); io.disconnect(); };
  }, [ref, token]);
}

export const FRAME = { zoom: 1, x: 50, y: 50 };
const DESKTOP_Q = "(min-width: 1024px)";

function useIsDesktop() {
  const [d, setD] = useState(() => typeof window !== "undefined" && window.matchMedia(DESKTOP_Q).matches);
  useEffect(() => {
    const m = window.matchMedia(DESKTOP_Q);
    const on = () => setD(m.matches);
    m.addEventListener("change", on);
    return () => m.removeEventListener("change", on);
  }, []);
  return d;
}

export const LAYOUT = { compact: "half", align: "left", text_size: "md", tint: "none", split: 50, side: "left", thumb: 96 };
export const styleFor = (ad, code) => ({ ...ad, layout: ad.placement_layouts?.[code] || ad.layout,
  image_fit: { ...(ad.image_fit || {}), ...(ad.placement_fits?.[code] || {}) } });
const HEX = /^#[0-9a-f]{6}$/i;
const tintStyle = (hex, tint) => {
  if (!HEX.test(hex || "") || tint === "none" || !tint) return undefined;
  if (tint === "border") return { borderColor: hex, boxShadow: `inset 0 0 0 1px ${hex}` };
  return { borderColor: `${hex}80`, backgroundImage: `linear-gradient(135deg, ${hex}40, ${hex}14)` };
};
const TITLE = { sm: "text-sm", md: "text-base", lg: "text-xl leading-tight" };
const TITLE_CARD = { sm: "text-sm", md: "text-base", lg: "text-2xl leading-tight" };
const onColor = (hex) => {
  const [r, g, b] = [1, 3, 5].map((i) => parseInt(hex.slice(i, i + 2), 16) / 255).map((v) => (v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4));
  return 0.2126 * r + 0.7152 * g + 0.0722 * b > 0.179 ? "#05070D" : "#FFFFFF";
};
export const ctaStyle = (hex) => (HEX.test(hex || "") ? { backgroundColor: hex, borderColor: hex, color: onColor(hex) } : undefined);
const COMPACT_KEY = { thumb: "square", half: "half", full: "full" };
export const frameKey = (compact, device, mode = "half") => (compact ? COMPACT_KEY[mode] : device === "desktop" ? "desktop" : "mobile");
export const frameBox = (key) => ({ square: "h-24 w-24 shrink-0 rounded-lg", half: "shrink-0 self-stretch min-h-[7.5rem]", full: "h-full w-full",
  mobile: "aspect-[16/9] w-full", desktop: "aspect-[16/7] w-full" }[key]);
const ALIGN = { left: ["text-left", "justify-start"], center: ["text-center", "justify-center"], right: ["text-right", "justify-end"] };

export function FramedImage({ src, alt, frame, className, testId, style }) {
  const f = { ...FRAME, ...(frame || {}) };
  return (
    <span className={`relative block overflow-hidden bg-white/5 ${className}`} style={style} data-testid={testId}>
      <img src={src} alt={alt} loading="lazy" draggable={false} className="absolute inset-0 h-full w-full select-none object-cover"
        style={{ objectPosition: `${f.x}% ${f.y}%`, transform: `scale(${f.zoom})`, transformOrigin: `${f.x}% ${f.y}%` }} />
    </span>
  );
}

export function AdCard({ ad, compact, mention, device }) {
  const ref = useRef(null);
  const pv = ad.preview;
  const isDesktop = useIsDesktop();
  const lay = { ...LAYOUT, ...(ad.layout || {}) };
  const mode = ad.image_url ? lay.compact : "none";
  const key = frameKey(compact, device || (isDesktop ? "desktop" : "mobile"), lay.compact);
  const [txt, row] = ALIGN[lay.align] || ALIGN.left;
  const full = compact && mode === "full";
  const shell = !compact ? "" : full ? "relative" : mode === "half" ? `flex items-stretch ${lay.side === "right" ? "flex-row-reverse" : ""}` : "flex items-center gap-3 p-3";
  const inner = !compact ? "p-4" : full ? `relative min-h-[7.5rem] p-4 ${{ left: "bg-gradient-to-r from-black/80 via-black/55 to-black/30", right: "bg-gradient-to-l from-black/80 via-black/55 to-black/30", center: "bg-black/60" }[lay.align]}` : mode === "half" ? "min-w-0 flex-1 p-3" : "min-w-0 flex-1";
  const img = ad.image_url && <FramedImage src={`${ORIGIN}${ad.image_url}`} alt={ad.alt} frame={ad.image_fit?.[key]} className={frameBox(key)} style={compact && mode === "half" ? { width: `min(${lay.split}%, calc(100% - 5.5rem))` } : compact && mode === "thumb" ? { width: lay.thumb, height: lay.thumb } : undefined} testId="ad-image" />;
  useViewable(ref, pv ? null : ad.token);
  const open = async () => {
    if (pv) { if (ad.url) window.open(ad.url, "_blank", "noopener,noreferrer"); else toast("Aperçu : aucun clic n'est compté"); return; }
    const w = window.open("", "_blank");
    const { data } = await send(ad.token, "click");
    if (!w) { if (data.url) window.location.assign(data.url); return; }
    w.opener = null;
    if (data.url) w.location.href = data.url; else w.close();
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(ad.code); toast.success("Code copié"); if (!pv) send(ad.token, "code_copied"); } catch { toast.error("Copie impossible sur cet appareil"); }
  };
  const report = async () => {
    if (pv) return;
    const reason = window.prompt("Pourquoi signaler cette offre ? (facultatif)") ?? null;
    if (reason === null) return;
    await send(ad.token, "report", reason.slice(0, 200));
    toast.success("Merci, l'offre sera vérifiée.");
  };
  const badge = (pv || ad.test) && <span className="rounded bg-amber-300 px-1.5 py-0.5 text-[9px] font-extrabold text-[#05070D]" data-testid="ad-test-badge">{ad.test && !pv ? "MODE TEST" : "APERÇU"}</span>;
  if (mention) {
    return (
      <p ref={ref} className="flex flex-wrap items-center gap-2 rounded-xl bg-white/[0.03] px-3 py-2 text-xs text-slate-300" data-testid="ad-card" aria-label={`${ad.label} : ${ad.title}`}>
        <span className="rounded bg-white/10 px-1.5 py-0.5 text-[10px] font-bold uppercase text-slate-200" data-testid="ad-label">{ad.label}</span>{badge}
        {ad.partner_logo_url && <img src={`${ORIGIN}${ad.partner_logo_url}`} alt="" className="h-4 w-4 shrink-0 rounded bg-white object-contain" data-testid="ad-partner-logo" />}
        <span className="min-w-0 flex-1 truncate">Proposé par <b className="text-white">{ad.partner}</b> · {ad.title}</span>
        <button type="button" onClick={open} className="font-semibold text-[#B7F52A] underline" data-testid="ad-cta">{ad.cta}</button>
      </p>
    );
  }
  return (
    <article ref={ref} className={`mpl-card overflow-hidden ${shell}`} data-testid="ad-card" data-layout={compact ? mode : "card"} data-align={lay.align} data-tint={ad.partner_color ? lay.tint : "none"} style={tintStyle(ad.partner_color, lay.tint)} aria-label={`${ad.label} : ${ad.title}`}>
      {full ? <div className="absolute inset-0">{img}</div> : img}
      <div className={`${inner} ${txt}`} data-testid="ad-text">
        <p className={`flex flex-wrap items-center gap-2 text-[10px] font-bold uppercase tracking-wider ${full ? "text-slate-200" : "text-slate-400"} ${row}`}>
          <span className="rounded bg-white/10 px-1.5 py-0.5 text-slate-200" data-testid="ad-label">{ad.label}</span>{badge}
          {ad.partner_logo_url && <img src={`${ORIGIN}${ad.partner_logo_url}`} alt={`Logo ${ad.partner}`} className="h-5 w-5 shrink-0 rounded bg-white object-contain p-0.5" data-testid="ad-partner-logo" />}
          <span className="min-w-0 truncate">{ad.partner}</span>
        </p>
        <p className={`mt-1 font-bold text-white ${(compact ? TITLE : TITLE_CARD)[lay.text_size] || TITLE.md} ${compact && mode !== "thumb" ? "line-clamp-2" : lay.text_size === "lg" ? "line-clamp-2" : "truncate"}`} data-testid="ad-title">{ad.title}</p>
        {compact && mode !== "thumb" && ad.subtitle && <p className={`line-clamp-1 text-xs ${full ? "text-slate-200" : "text-slate-400"}`}>{ad.subtitle}</p>}
        {!compact && ad.subtitle && <p className="text-sm text-slate-300">{ad.subtitle}</p>}
        {!compact && ad.body && <p className="mt-1 text-xs text-slate-400">{ad.body}</p>}
        {!compact && ad.offer?.conditions && <p className="mt-1 text-[11px] text-slate-500">Conditions : {ad.offer.conditions}{ad.offer.valid_until && ` · jusqu'au ${ad.offer.valid_until}`}</p>}
        <div className={`mt-2 flex flex-wrap items-center gap-2 ${row}`}>
          <button type="button" onClick={open} className="mpl-btn-primary px-3 py-1.5 text-xs" style={ctaStyle(ad.partner_color)} data-testid="ad-cta"><ExternalLink size={12} /> {ad.cta}</button>
          {ad.code && <button type="button" onClick={copy} className="mpl-btn-ghost px-3 py-1.5 font-mono text-xs" data-testid="ad-code"><Copy size={12} /> {ad.code}</button>}
          {ad.code && !ad.code_verified && !compact && <span className="text-[10px] text-slate-500">Code fourni par le partenaire, validité non garantie</span>}
          {!pv && <button type="button" onClick={report} className={`${lay.align === "left" ? "ml-auto" : ""} ${full ? "text-slate-300" : "text-slate-500"} hover:text-slate-200`} aria-label="Signaler cette offre" data-testid="ad-report"><Flag size={13} /></button>}
        </div>
      </div>
    </article>
  );
}

export default function AdSlot({ placement, leagueId, compact = false, mention = false, className = "" }) {
  const [ads, setAds] = useState([]);
  useEffect(() => {
    let alive = true;
    api.get("/ads/serve", { params: { placement, ...(leagueId ? { league_id: leagueId } : {}) } }).then(({ data }) => { if (alive) setAds(data.ads || []); }).catch(() => {});
    return () => { alive = false; };
  }, [placement, leagueId]);
  if (!ads.length) return null;
  return (
    <div className={`space-y-2 ${className}`} data-testid={`ad-slot-${placement}`}>
      {ads.map((a) => <AdCard key={a.token} ad={a} compact={compact} mention={mention} />)}
      {placement !== "offers_page" && !mention && <Link to="/offres" className="block text-right text-[11px] text-slate-500 hover:text-slate-300" data-testid="ad-all-offers">Toutes les offres partenaires</Link>}
    </div>
  );
}
